/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ComponentProps } from 'react';
import { ChevronDownIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

type NativeSelectProps = Omit<ComponentProps<'select'>, 'size'> & {
    size?: 'sm' | 'default';
};

function NativeSelect({ className, size = 'default', ...props }: NativeSelectProps) {
    return (
        <div className="group/native-select relative w-fit" data-slot="native-select-wrapper">
            <select
                data-slot="native-select"
                data-size={size}
                className={cn(
                    'w-full min-w-0 appearance-none rounded-xs border border-input bg-background px-3 py-3.5 pr-9 text-sm leading-5 text-foreground shadow-xs outline-none transition-[background-color,border-color,box-shadow,color] hover:bg-secondary disabled:pointer-events-none disabled:cursor-not-allowed disabled:border-input disabled:bg-background disabled:text-muted-foreground data-[size=sm]:h-8 data-[size=sm]:py-1 data-[size=sm]:pr-7',
                    'placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground',
                    'focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-focus',
                    'aria-invalid:border-destructive aria-invalid:shadow-none aria-invalid:ring-3 aria-invalid:ring-destructive-focus disabled:aria-invalid:border-input disabled:aria-invalid:shadow-xs focus-visible:aria-invalid:border-destructive focus-visible:aria-invalid:ring-destructive-focus',
                    className
                )}
                {...props}
            />
            <ChevronDownIcon
                className="pointer-events-none absolute top-1/2 right-3.5 size-4 -translate-y-1/2 text-muted-foreground select-none"
                aria-hidden="true"
                data-slot="native-select-icon"
            />
        </div>
    );
}

function NativeSelectOption(props: ComponentProps<'option'>) {
    return <option data-slot="native-select-option" {...props} />;
}

function NativeSelectOptGroup({ className, ...props }: ComponentProps<'optgroup'>) {
    return <optgroup data-slot="native-select-optgroup" className={cn(className)} {...props} />;
}

export { NativeSelect, NativeSelectOptGroup, NativeSelectOption };
